import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import { supabase } from '../lib/supabase';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function WeeklyInsights() {
  const location = useLocation();
  const navigate = useNavigate();
  const [lessonText, setLessonText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  if (!location.state) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold text-red-600 mb-4">Missing data</h2>
        <p className="text-gray-700 mb-4">
          This page only works if you arrive here from the Weekly Summaries page.
        </p>
        <Button onClick={() => navigate('/weekly-summaries')}>Go Back</Button>
      </div>
    );
  }

  const { weekRange, stats, analysis, hasData } = location.state;

  if (!hasData) {
    return <div className="p-6 text-gray-700">No data available for this week.</div>;
  }

  const handleSaveLesson = async () => {
    setIsSaving(true);
    setSaveMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) throw new Error('User not authenticated');

      const { error } = await supabase.from('lessons_learned').insert([
        {
          user_id: userId,
          lesson: lesson,
          date: new Date().toISOString()
        }
      ]);
      if (error) throw error;
      setLessonText('');
      setSaveMessage('✅ Lesson saved!');
    } catch (err: any) {
      console.error('Error saving lesson:', err.message);
      setSaveMessage('❌ Failed to save lesson.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ProtectedLayout>
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h1 className="text-3xl font-bold text-[#004060]">
        Weekly Insights: {weekRange.start} – {weekRange.end}
      </h1>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold text-[#236caf] mb-2">Celebration</h2>
        <p>{analysis.celebration}</p>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold text-[#236caf] mb-2">Patterns</h2>
        <p>{analysis.patterns}</p>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold text-[#236caf] mb-2">Suggestions</h2>
        <ul className="list-disc ml-5">
          {analysis.suggestions.map((s: string, idx: number) => (
            <li key={idx}>{s}</li>
          ))}
        </ul>
      </div>

      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold text-[#236caf] mb-2">Summary</h2>
        <p>{analysis.summary}</p>
      </div>

      {/* Lessons Learned Box */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="text-xl font-semibold text-[#236caf] mb-2">Your Lessons Learned</h2>
        <textarea
          className="w-full border border-gray-300 rounded p-3 resize-none h-24 text-gray-800"
          placeholder="What insight or lesson are you taking from this week?"
          value={lessonText}
          onChange={(e) => setLessonText(e.target.value)}
        />
        <div className="flex items-center justify-between mt-3">
          <Button
            onClick={handleSaveLesson}
            disabled={!lessonText.trim() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Lesson'}
          </Button>
          {saveMessage && (
            <span className="text-sm text-gray-600 ml-4">{saveMessage}</span>
          )}
        </div>
      </div>

      {/* Return to Dashboard */}
      <div className="flex justify-center mt-8">
        <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
      </div>
    </div>
    </ProtectedLayout>
  );
}
